import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Form, FormControl, Modal, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../store";
import {
  getMasterRaceInfo,
  hideStudentEditModal,
  updateTeacherStudentDetails,
} from "../../../../../store/onboarding/actions";
import {
  genderList,
  Race,
  ReactSelectOption,
  Student,
} from "../../../../../store/onboarding/types";
import Select from "react-select";
import { useLoading } from "../../../../../utils/hooks/useLoading";
import useUserRole from "../../../../../utils/hooks/useUserRole";
import { allowAlphaNumbers, escapeSpecialCharacter, splitStudentName } from "../../../../../utils/NamesUtils";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

type OwnProps = {};

type Props = OwnProps;

const StudentEditModal: FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();

  const show = useSelector<ApplicationState, boolean | undefined>(
    (s) => s.onboarding.modalsState.showStudentEditModal
  );

  const student = useSelector<ApplicationState, Student | undefined>(
    (s) => s.onboarding.selectedStudent
  );
  const hasUpdatedStudentID = useRef(false);

  const [isUpdateStudentID, setUpdateStudentID] = useState<boolean>();

  const [firstName, setFirstName] = useState<string | undefined>();
  const [lastName, setLastName] = useState<string | undefined>();
  const [uniqueId, setUniqueId] = useState<string | undefined>();
  const [race, setRace] = useState<number | undefined>();
  const [raceOtherValue, setRaceOtherValue] = useState<string | undefined>();
  const [gender, setGender] = useState<string | undefined>();
  const [freeOrReducedLunch, setFreeOrReducedLunch] = useState<boolean | undefined>(false);
  const [specialEducation, setSpecialEducation] = useState<boolean | undefined>(false);
  const [ell, setELL] = useState<boolean | undefined>(false);
  const [sld, setSLD] = useState<boolean | undefined>(false);


  const { isCoach, isTeacher, isDistrictOwner } = useUserRole();

  const masterRaceInfo = useSelector(
    (s: ApplicationState) => s.onboarding.masterRaceInfo
  );
  useEffect(() => {
    if(show) {
      dispatch(getMasterRaceInfo())
      setUpdateStudentID(false);
      hasUpdatedStudentID.current = false;
    }
  },[show]);

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const sanitizedValue = escapeSpecialCharacter(value);
    setFirstName(sanitizedValue);
  };
  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const sanitizedValue = escapeSpecialCharacter(value);
    setLastName(sanitizedValue);
  };
  const handleUniqueIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const sanitizedValue = allowAlphaNumbers(value);
    setUniqueId(sanitizedValue);
    if((student?.unique_id !== sanitizedValue) && (isUpdateStudentID == false) && (hasUpdatedStudentID.current == false)) {
        setUpdateStudentID(true);
        hasUpdatedStudentID.current = true;
      }
  };

  const handleModalShow = () => {
    if (student) {
      setFirstName(student.first_name);
      setLastName(student.last_name);
      setUniqueId(student.unique_id);
      setRace(student.race);
      setRaceOtherValue(student.race_other_value);
      setGender(student.gender);
      setSpecialEducation(student.special_education);
      setELL(student.ell);
      setSLD(student.sld);
      setFreeOrReducedLunch(student.free_or_reduced_lunch);
    }
  };
  const handleModalClose = () => {
    dispatch(hideStudentEditModal());
  };

  const isLoadingUpdateStudentDetails = useSelector<
    ApplicationState,
    boolean | undefined
  >((s) => s.onboarding.isLoading.updateStudentDetails);
  const errorUpdateStudentDetails = useSelector<
    ApplicationState,
    string | undefined
  >((s) => s.onboarding.errors.updateStudentDetails);
  useLoading(
    isLoadingUpdateStudentDetails,
    errorUpdateStudentDetails,
    handleModalClose
  );

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const updatedStudent: Student = {
      id: student?.id,
      unique_id: uniqueId,
      first_name: firstName!,
      last_name: lastName!,
      race: race,
      race_other_value: raceOtherValue,
      gender: gender,
      special_education: specialEducation!,
      ell: ell!,
      sld: sld!,
      free_or_reduced_lunch: freeOrReducedLunch
    };
    dispatch<any>(updateTeacherStudentDetails(updatedStudent)).then((updatedStudent: Student) => {
      if(updatedStudent && hasUpdatedStudentID.current) {
       toastr.success('Success', `Student details updated successfully.`);
      }
    }).catch((error:string) => {
      console.error('Error updating student details:', error);
    });
  };

  const options: ReactSelectOption<number>[] = useMemo(() => {
    if(masterRaceInfo?.races?.length) {
      return masterRaceInfo?.races?.map((race, index) => ({
        value: index, label: race 
     }))
    } else {
      return [];
    }
  },[masterRaceInfo?.races]);

  const genderOptions: ReactSelectOption<string>[] = useMemo(() => genderList, []);

  return (
    <>
    <Modal
      animation={false}
      backdropClassName="customDarkModalBackdrop in"
      show={show}
      onShow={handleModalShow}
      onHide={handleModalClose}
      centered
    >
      <Modal.Header closeButton className="purpleModalHeader orangeModalHeader">
        <Modal.Title>Edit Student</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(isCoach || isTeacher) && (
          <div className="d-flex justify-content-center mb-4">
            <FontAwesomeIcon
                    icon={faLock}
                    style={{ fontSize: "16px", margin: "2px 5px 0px 0px" }}
                  />
            <span>Some information is read-only for your account type.</span>
          </div>
        )}
        <Form onSubmit={handleFormSubmit}>
         <Form.Group>
            <Form.Label className="font-weight-bold text-left">
              First Name
            </Form.Label>{isDistrictOwner ? <span className="label-asterisk">*</span> : ''}
            <Form.Control
              required
              value={firstName}
              onChange={handleFirstNameChange}
              name={"firstName"}
              placeholder="Enter"
              type="text"
              disabled={isCoach || isTeacher}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="font-weight-bold text-left">
              Last Name
            </Form.Label>{isDistrictOwner ? <span className="label-asterisk">*</span> : ''}
            <Form.Control
              required
              value={lastName}
              onChange={handleLastNameChange}
              name={"lastName"}
              placeholder="Enter"
              type="text"
              disabled={isCoach || isTeacher}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="font-weight-bold text-left">
              Student ID
            </Form.Label>{isDistrictOwner ? <span className="label-asterisk">*</span> : ''}
            <Form.Control
              required
              value={uniqueId}
              onChange={handleUniqueIdChange}
              name={"uniqueId"}
              placeholder="Enter"
              disabled={isCoach || isTeacher}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="font-weight-bold text-left">Race</Form.Label>
            <Select
              isClearable
              className="flex-1"
              placeholder="Not Set"
              options={options}
              value={options.find((r) => r.value === race)}
              onChange={(value: any) => {
                setRace(value ? value.value : undefined);
                setRaceOtherValue(undefined);
              }}
            />
            {race === Race.Other && (
              <FormControl
                className="mt-1"
                placeholder="Enter Race Name..."
                value={raceOtherValue ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setRaceOtherValue(value);
                }}
              />
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label className="font-weight-bold text-left">Gender</Form.Label>
            <Select
              isClearable
              className="flex-1"
              placeholder="Not Set"
              options={genderOptions}
              value={genderOptions.find((g) => g.value === gender)}
              onChange={(value: any) => {
                setGender(value ? value.value : undefined);
              }}
            />
          </Form.Group>
          <Form.Group>
            <div className="d-flex align-items-center justify-content-between w-100">
              Special Education
              <Form.Check
                type="switch"
                id="special_education"
                checked={specialEducation ?? false}
                onChange={(e) => setSpecialEducation(e.target.checked)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-between w-100">
              ELL
              <Form.Check
                type="switch"
                id="ell"
                checked={ell ?? false}
                onChange={(e) => setELL(e.target.checked)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-between w-100">
              SLD
              <Form.Check
                type="switch"
                id="sld"
                checked={sld ?? false}
                onChange={(e) => setSLD(e.target.checked)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-between w-100">
              Free or Reduced Lunch
              <Form.Check
                type="switch"
                id="freeOrReducedLunch"
                checked={freeOrReducedLunch ?? false}
                onChange={(e) => setFreeOrReducedLunch(e.target.checked)}
              />
            </div>
          </Form.Group>
          <hr />
          <div className="modalActions">
            <button
              className="whiteBtnSm"
              type="reset"
              onClick={handleModalClose}
            >
              Cancel
            </button>
            <button className="blueBtnSm" type="submit" disabled={(!firstName?.trim() || !lastName?.trim() || !uniqueId?.trim()) && isDistrictOwner} >
              Save
              {isLoadingUpdateStudentDetails && (
                <Spinner animation="border" size="sm" className="ml-1" />
              )}
            </button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
    {isUpdateStudentID ? 
      <Modal
        animation={false}
        backdropClassName="customDarkModalBackdrop inner in"
        show={isUpdateStudentID}
        onHide={() => {
          setUniqueId(student?.unique_id);
          setUpdateStudentID(false);
        }}
        centered
      >
        <Modal.Header closeButton className="purpleModalHeader orangeModalHeader">
          <Modal.Title>Confirm Student ID Change</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-center mb-1">
            Are you sure you want to change the Student ID for <span className="font-weight-bold">{firstName+' '+lastName}</span>?
          </p>
          <p>This update will apply across all areas where this student’s record appears, such as data periods and intervention groups.</p>  
          <hr/>
          <div className="btnActions">
            <button className="whiteBtnSm" onClick={() => {
              setUniqueId(student?.unique_id);
              setUpdateStudentID(false);
            }}>Cancel</button>
            <button className="blueBtnSm" onClick={() => setUpdateStudentID(false)}>
            UPDATE ID</button>
          </div>
        </Modal.Body>
      </Modal>
    : ''}
    </>

  );
};

export default StudentEditModal;
